import type { KitchenOrganizationSummary } from '@/api/kitchen-settings'
import type { SchoolOnboardingTaskSummary } from '@/api/school-onboarding'

export interface MissingOnboardingDocuments {
  taskId: string
  kitchenName: string
  labels: string[]
}

/** Match the onboarding manager's CHEF_ADMIN upload permission per organization.
 * Required uploads keep prompting until present. Optional-only document tasks
 * prompt while unanswered; completing one without an upload is a valid opt-out.
 * The API also supplies the default document form for chefs without assigned tasks.
 */
export function missingChefOnboardingDocuments(
  kitchens: ReadonlyArray<Pick<KitchenOrganizationSummary, 'id' | 'name' | 'profileCodes'>>,
  tasks: ReadonlyArray<SchoolOnboardingTaskSummary>,
): MissingOnboardingDocuments[] {
  const adminKitchens = new Map(
    kitchens.filter((kitchen) => kitchen.profileCodes.includes('CHEF_ADMIN')).map((k) => [k.id, k.name]),
  )

  return tasks.flatMap((task) => {
    const kitchenName = adminKitchens.get(task.organizationId)
    if (kitchenName === undefined || task.status === 'CANCELED') {
      return []
    }
    const uploads = task.formDefinition.fields.filter((field) => field.type === 'document_upload')
    const hasUpload = (fieldId: string) => {
      const value = task.response?.[fieldId]
      return Array.isArray(value) && value.some((doc) => doc && typeof doc.id === 'string' && doc.id.length > 0)
    }
    const requiredUploads = uploads.filter((field) => field.required)
    const missing = requiredUploads.length
      ? requiredUploads.filter((field) => !hasUpload(field.id))
      : task.status !== 'COMPLETED' && !uploads.some((field) => hasUpload(field.id))
        ? uploads
        : []

    return missing.length ? [{ taskId: task.id, kitchenName, labels: missing.map((field) => field.label) }] : []
  })
}
